import React, { useMemo } from 'react';
import {
  OfflineBoltRounded,
  LocalFireDepartmentRounded,
} from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import RankNumber from '@src/containers/VoiceLeaderboard/RankingList/RankNumber';
import RcTooltip from 'rc-tooltip';
import Typography from '@src/new-components/Typography';
import vbeeLogo from '@src/assets/logo/logo-vbee.svg';
import verifiedIcon from '@src/assets/icons/verified-rounded.svg';
import AudioAvatar from '@src/new-components/Avatar/AudioAvatar';
import { VOICE_PROVIDER } from '@src/constants/voice';
import { VOICE_CLONE_TYPE } from '@src/constants/voiceCloning';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import { TRENDING_VOICE_CREDITS } from '@src/constants/voiceLibrary';
import { LANGUAGE } from '@src/constants';
import theme from '@src/styles/theme';
import { BREAKPOINTS } from '@src/styles/config';
import { getFrameVoiceUrl } from '@src/services/voiceCloning';
import LimitedFeature from '@src/components/LimitedFeature';
import { VoiceInfoContainer } from './index.style';
import VoiceDescription from './VoiceDescription';
import VoicePopover from '../VoicePopover';

export const BadgeIcon = React.memo(({ provider, type }) => {
  if (provider && provider === VOICE_PROVIDER.VBEE) {
    return <img src={vbeeLogo} alt="vbee-logo" className="avatar-badge-icon" />;
  }

  if (type === VOICE_CLONE_TYPE.ZERO_SHOT) {
    return (
      <OfflineBoltRounded
        className="avatar-badge-icon"
        sx={{
          color: COLOR.darkBlue,
          backgroundColor: COLOR.white,
          borderRadius: '50%',
        }}
      />
    );
  }

  if (provider === VOICE_PROVIDER.VBEE_VOICE_CLONING) {
    return (
      <img
        src={verifiedIcon}
        alt="pro-voice-cloning"
        className="avatar-badge-icon"
      />
    );
  }

  return null;
});

const VoiceInfo = ({
  voice,
  retentionDaysLabel,
  variant,
  isHovered = false,
  isTrending,
  isVbee,
  onPlayAudio,
  onPauseAudio,
  isPlaying,
  isLeaderboard,
  prizes,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MMD));
  const isVbeeVoice = useMemo(
    () =>
      voice?.provider === VOICE_PROVIDER.VBEE ||
      voice?.provider === VOICE_PROVIDER.AMAZON ||
      voice?.provider === VOICE_PROVIDER.GOOGLE ||
      voice?.provider === VOICE_PROVIDER.MICROSOFT,
    [voice],
  );
  const notShowPopover = (isVbeeVoice && !voice?.description) || isMobile;

  const getTrendingLabel = () => {
    if (isTrending && voice.stats?.totalCredits >= TRENDING_VOICE_CREDITS) {
      return (
        <div className="trending-label">
          <LocalFireDepartmentRounded
            sx={{
              color: COLOR.functionalPink.default,
              fontSize: 16,
            }}
          />
          <Typography variant="body-12-medium" color={COLOR.tagNew}>
            {t('featuredVoice')}
          </Typography>
        </div>
      );
    }
    return null;
  };
  const renderCreditsFactor = () => {
    if (voice.creditFactor > 1) {
      return (
        <div className="credit-factor-label">
          <Typography
            variant="body-12-medium"
            color={COLOR.blueV2}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('doubleChar', {
              creditFactor: voice?.creditFactor,
            })}
          </Typography>
        </div>
      );
    }
    return null;
  };

  return (
    <RcTooltip
      mouseEnterDelay={0.8}
      trigger={[notShowPopover || isLeaderboard ? '' : 'hover']}
      showArrow={false}
      align={{
        offset: [0, 0],
      }}
      placement="rightTop"
      overlay={
        <VoicePopover
          voice={voice}
          retentionDaysLabel={retentionDaysLabel}
          isVbee={isVbee}
          prizes={prizes}
        />
      }
      zIndex={9999}
    >
      <VoiceInfoContainer isVietnamese={language === LANGUAGE.VN}>
        {isLeaderboard && <RankNumber rank={voice?.rank} />}
        <AudioAvatar
          avatar={voice?.avatar || voice?.image || voice?.roundImage}
          size={40}
          audioUrl={voice?.demo}
          audioIconColor={COLOR.yellow.default}
          badgeIcon={
            <BadgeIcon provider={voice?.provider} type={voice?.type} />
          }
          styleBadgeIcon={{ height: '16px !important', zIndex: 10 }}
          isHovered={isHovered}
          onPlayAudio={onPlayAudio}
          onPauseAudio={onPauseAudio}
          isPlaying={isPlaying}
          frameImageUrl={getFrameVoiceUrl(voice)}
        />
        <div className="voice-info-container">
          <div className="voice-name-container">
            <Typography
              variant="body-16-medium"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: COLOR.darkGray,
              }}
            >
              {voice.name}
            </Typography>
            <LimitedFeature voiceFeatures={voice?.features} />
            {renderCreditsFactor()}
            {getTrendingLabel()}
          </div>
          <VoiceDescription
            category={voice.category}
            gender={voice.gender}
            ageGroup={voice?.ageGroup}
            variant={variant}
          />
        </div>
      </VoiceInfoContainer>
    </RcTooltip>
  );
};

export default VoiceInfo;
